import z from 'zod'
import { PrePayMethodsSchema } from '../../schema/PaymentSchema'
import { idSchema } from '../../schema/remoteConfigParams'
import { orderRefractionSchema, PartialAddressSchema } from '../../schema'
import { makeErpPaginatedSchema } from './pagination'
// From erp but without openapi annotations

// Regex to remove openapi annotations
// /\.openapi\(\{[.\n\w\s:',]+\}\)/

export const ErpOrderState = {
  draft: 'draft',
  sent: 'sent', // if we treat draft as created quotation, the sent means the quotation is sent to the system
  submit: 'submit', // (submitted for payment), similar like pending, it was used by and old app and POS. it has payment related but it might not be fullfilled
  pending: 'pending', // can be not-paid and there (in some regions) is a 2h timeout before the factory starts working on it (cancel window)
  sale: 'sale', // already paid and the factory is (will be) working on it
  done: 'done', // might be already picked up by the customer
  cancel: 'cancel',
  unknown: 'unknown',

  // WARN: guessing, these states arenot confirmed, we need to check what the API actually returns
  ship: 'ship', // duplicate of shipping
  shipping: 'shipping', // the order is on the way to the store/customer
  ready: 'ready', // glasses arrived to the store and are ready to be picked up by the customer
  pick: 'pick', // duplicate of picked
  picked: 'picked', // the customer has picked up the glasses
} as const

export type OrderStatusValue =
  (typeof ErpOrderState)[keyof typeof ErpOrderState]

export function isValidOrderStatusValue(
  value: string
): value is OrderStatusValue {
  return (Object.values(ErpOrderState) as readonly string[]).includes(value)
}

export const OrderStatusGroups = {
  all: 'all',
  draft: 'draft',
  attention: 'attention',
  ongoing: 'ongoing',
  active: 'active',
  completed: 'completed',
  cancelled: 'cancelled',
} as const

export const orderStatusesByGroup: Record<
  OrderStatusGroup,
  OrderStatusValue[]
> = {
  all: [], // meaning all statuses
  draft: ['draft', 'sent'],
  attention: ['submit', 'pending'],
  active: ['sale', 'ship', 'shipping', 'ready'],
  completed: ['pick', 'picked', 'done'],
  cancelled: ['cancel'],

  ongoing: ['submit', 'pending', 'sale', 'ship', 'shipping', 'ready'], // this is merge of attention and active, left for backwards compatibility
}

export type OrderStatusGroup =
  (typeof OrderStatusGroups)[keyof typeof OrderStatusGroups]

export const defaultOrderStatusGroup: OrderStatusGroup = OrderStatusGroups.all

export const OrderPaymentOverviewValues = {
  none: 'none',
  partially: 'partially',
  paid: 'paid',
} as const

export type OrderPaymentOverview =
  (typeof OrderPaymentOverviewValues)[keyof typeof OrderPaymentOverviewValues]

export const OrderPaymentStatusValues = {
  draft: 'draft',
  posted: 'posted',
  pending: 'pending',
  declined: 'declined',
  unknown: 'unknown',
} as const

export type OrderPaymentStatus =
  (typeof OrderPaymentStatusValues)[keyof typeof OrderPaymentStatusValues]

export const isValidOrderPaymentStatusValue = (
  value: string
): value is OrderPaymentStatus => {
  return (
    Object.values(OrderPaymentStatusValues) as readonly string[]
  ).includes(value)
}

// Statuses that represent money actually collected. Only these count towards
// the paid amount of an order — pending/declined/draft transactions do not.
export const successfulOrderPaymentStatuses = [
  OrderPaymentStatusValues.posted,
] as const satisfies readonly OrderPaymentStatus[]

export const isSuccessfulOrderPaymentStatus = (value: string): boolean =>
  (successfulOrderPaymentStatuses as readonly string[]).includes(value)

export const isDeclinedOrderPaymentStatus = (value: string): boolean =>
  value === OrderPaymentStatusValues.declined

export const activeOrderPaymentStatuses = [
  OrderPaymentStatusValues.draft,
  OrderPaymentStatusValues.pending,
] as const satisfies readonly OrderPaymentStatus[]

export const isActiveOrderPaymentStatus = (value: string): boolean =>
  (activeOrderPaymentStatuses as readonly string[]).includes(value)

export const getOrderPaymentOverview = (
  items: readonly { price: number }[],
  payments: readonly { amount: number; status: string }[]
): {
  total: number
  paid: number
  status: OrderPaymentOverview
  hasDeclined: boolean
} => {
  const total = items.reduce((acc, item) => acc + item.price, 0)
  const paid = payments.reduce(
    (acc, payment) =>
      isSuccessfulOrderPaymentStatus(payment.status)
        ? acc + payment.amount
        : acc,
    0
  )
  const hasDeclined = payments.some((payment) =>
    isDeclinedOrderPaymentStatus(payment.status)
  )

  const status =
    paid >= total && total > 0
      ? OrderPaymentOverviewValues.paid
      : paid > 0
      ? OrderPaymentOverviewValues.partially
      : OrderPaymentOverviewValues.none

  return { total, paid, status, hasDeclined }
}

export const OrderPaymentTypes = {
  inbound: 'inbound',
  unknown: 'unknown',
  // TODO: add order payment types
} as const

export type OrderPaymentType =
  (typeof OrderPaymentTypes)[keyof typeof OrderPaymentTypes]

export const isValidOrderPaymentTypeValue = (
  value: string
): value is OrderPaymentType => {
  return (Object.values(OrderPaymentTypes) as readonly string[]).includes(value)
}

export const ErpOrderStateSchema = z.nativeEnum(ErpOrderState)

export type ErpOrderState = z.infer<typeof ErpOrderStateSchema>

export const ErpCustomerSchema = z.object({
  id: z.number(),
  title: z.string().nullish(),
  country_code: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email().nullish().catch(null),
  mobile: z.string().nullish().catch(null),
  phone: z.string().nullish().catch(null),
  // Sometimes we have date other times we have birth_date
  date: z.string().date().nullish().catch(undefined),
  birth_date: z.string().date().nullish().catch(null),
  email_state: z.boolean().catch(false),
  phone_state: z.boolean().catch(false),
  // referrer_id: z.string().nullable(),
  // address: AddressSchema.nullable(),
  street: z.string().nullable(),
  street2: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  ref: z.string(),
  referrer_id: z.string().nullish(),
  credit: z.number(),
  vat: z.string().nullish(),
  is_kids: z.boolean().nullish().catch(false),
})

export type ErpCustomer = z.infer<typeof ErpCustomerSchema>

export const ErpAddressSchema = z.object({
  id: z.number(),
  customer_id: idSchema,
  first_name: z.string(),
  last_name: z.string().nullable(),
  email: z.string().email().nullable().catch(null),
  mobile: z.string().nullable().catch(null),
  birth_date: z.string().nullable().catch(null),
  date: z.string().nullable().catch(null),
  country_code: z.string(),
  street: z.string(),
  street2: z.string().nullable(),
  zip: z.string(),
  city: z.string(),
  vat: z.string().optional(),
  type: z.enum(['invoice', 'delivery']).catch('delivery'),
})

export type ErpAddress = z.infer<typeof ErpAddressSchema>

export type ErpAddressCreate = Omit<ErpAddress, 'id' | 'birth_date'>

// For create and path
export type ErpCustomerRequestPayload = Omit<
  ErpCustomer,
  'id' | 'credit' | 'ref' | 'referrer_id' | 'email_state' | 'phone_state'
> & { prefix: string | null }

export type PartialAddress = z.infer<typeof PartialAddressSchema>

export type Customer = Omit<
  ErpCustomer,
  'street' | 'street2' | 'city' | 'zip' | 'country_code' | 'date' | 'birth_date'
> & {
  birth_date?: string | null
  address: PartialAddress
}

export type InvoiceAddress = Customer & {
  vat?: string
}

export const mockCustomer: ErpCustomer = {
  id: 1,
  title: 'male',
  first_name: 'John',
  last_name: 'Doe',
  email: 'example@supervista.de',
  mobile: '123456789',
  phone: '+49',
  date: new Date('1990-01-01').toISOString(),
  ref: '1',
  zip: '12345',
  street: 'Street',
  street2: 'Street 2',
  city: 'Berlin',
  country_code: 'DE',
  credit: 100,
  referrer_id: null,
  email_state: true,
  phone_state: true,
}

export const mockCustomer2: ErpCustomer = {
  id: 2,
  title: 'female',
  first_name: 'Jane',
  last_name: 'Doe',
  email: 'example2@supervista.de',
  mobile: '123456789',
  phone: '+49',
  date: new Date('2000-12-12').toISOString(),
  ref: '2',
  zip: '12345',
  city: 'Berlin',
  street: 'Street',
  street2: 'Street 2',
  country_code: 'DE',
  credit: 0,
  referrer_id: null,
  email_state: true,
  phone_state: true,
}

export const mockStaff: Staff = {
  id: 1,
  ref: '1',
  name: 'John Doe',
  store_id: '0221339',
}

export const StaffSchema = z.object({
  id: idSchema,
  ref: z.string(),
  name: z.string(),
  store_id: idSchema,
})

export const StoreTypeSchema = z
  .enum(['EXCLUSIVE', 'GENERAL', 'OWN', 'EXPRESS', 'DSH'])
  // We don't want to throw an error here as it will break the app
  .catch('GENERAL')

export type StoreType = z.infer<typeof StoreTypeSchema>

export const OptometristSchema = z.object({
  id: z.number(),
  name: z.string(),
})

export const ErpStoreStaffSchema = z.object({
  id: z.number(),
  name: z.string(),
  role: z.string(),
})

export type ErpStoreStaff = z.infer<typeof ErpStoreStaffSchema>

export const ErpStoreSchema = z.object({
  id: z.number(),
  ref: z.string(),
  name: z.string(),
  optometrists: z.array(OptometristSchema),
  type: StoreTypeSchema,
  prepay_method: PrePayMethodsSchema,

  address: PartialAddressSchema.omit({ country_code: true })
    .extend({
      country: z.string(),
    })
    .nullish(),
})

export const StoreSchema = ErpStoreSchema.extend({
  region: z.string(),
})

export const mockStore: Store = {
  id: 1,
  ref: '0221339',
  name: 'Königs Wusterhausen',
  type: 'EXCLUSIVE',
  region: 'de',
  optometrists: [
    {
      id: 1,
      name: 'Optician',
    },
    {
      id: 2,
      name: 'Refractionist',
    },
  ],
  prepay_method: [
    {
      key: 'terminal',
      name: 'Terminal',
    },
    {
      key: 'paylink',
      name: 'Paylink',
    },
    {
      key: 'invoice',
      name: 'Invoice',
    },
  ],
}

export type ErpStore = z.infer<typeof ErpStoreSchema>

export type Store = z.infer<typeof StoreSchema>

export type Staff = z.infer<typeof StaffSchema>

export const getErpDate = (dateErp: string) => {
  // dateErp format: "YYYY-MM-DD HH:MM"
  const split = dateErp.split(' ')
  const date = split[0]
  const time = split[1]

  let seconds = ''
  if (time.length === 5) seconds = ':00'

  return new Date(`${date}T${time}${seconds}.000Z`)
}

export const AppointmentSchema = z.object({
  success: z.boolean(),
  sequence: z.string(),
})

export type Appointment = z.infer<typeof AppointmentSchema>

export const ErpCustomerDiscountCodeSchema = z.object({
  code: z.string(),
  group: z.string().nullish(),
  description: z.string(),
  expiration_date: z.string().optional(),
})

export type ErpCustomerDiscountCode = z.infer<
  typeof ErpCustomerDiscountCodeSchema
>

// New types for order history endpoints
export const ErpOrderRefractionSchema = orderRefractionSchema.extend({
  name: z.string(),
  parent_id: z.string(),
  // Override some fields to match ERP API response types
  bvd: z.coerce.number().nullable().catch(null),
  ffa: z.coerce.number().nullable().catch(null),
  pa: z.coerce.number().nullable().catch(null),
  rd: z.coerce.number().nullable().catch(null),
  remark: z.string().optional(),
  thickness_balance: z.boolean().nullable().catch(null),
  x_ext: z.coerce.number().nullable().catch(null),
  y_ext: z.coerce.number().nullable().catch(null),
  // Override some numeric fields that come as strings from ERP
  r_base_curve: z.coerce.number().nullable().catch(null),
  l_base_curve: z.coerce.number().nullable().catch(null),
  r_decentration: z.coerce.number().nullable().catch(null),
  l_decentration: z.coerce.number().nullable().catch(null),
  r_et: z.coerce.number().nullable().catch(null),
  l_et: z.coerce.number().nullable().catch(null),
  r_ct: z.coerce.number().nullable().catch(null),
  l_ct: z.coerce.number().nullable().catch(null),
  r_cyl: z.coerce.number().nullable().catch(null),
  l_cyl: z.coerce.number().nullable().catch(null),
  r_axis: z.coerce.number().nullable().catch(null),
  l_axis: z.coerce.number().nullable().catch(null),
  r_prism1: z.coerce.number().nullable().catch(null),
  l_prism1: z.coerce.number().nullable().catch(null),
  r_prism2: z.coerce.number().nullable().catch(null),
  l_prism2: z.coerce.number().nullable().catch(null),
  r_visus: z.coerce.number().nullable().catch(null),
  l_visus: z.coerce.number().nullable().catch(null),
  // Override direction fields to match ERP API response
  right_na_te: z.coerce.number().nullable().catch(null),
  right_ha_ba: z.coerce.number().nullable().catch(null),
  left_na_te: z.coerce.number().nullable().catch(null),
  left_ha_ba: z.coerce.number().nullable().catch(null),
  optometrist_name: z.string().nullable().catch(null).optional(),
})

export const ErpOrderHistoryConfigSchema = z.object({
  color: z.string(),
  corridor: z.string(),
  distance: z.string(),
  first_id: z.boolean(),
  frame_from_stock: z.boolean(),
  inventory_no: z.string(),
  l_fh: z.number(),
  lens: z.string(),
  lifestyle: z.string(),
  mirror: z.string(),
  premium_insurance: z.boolean(),
  r_fh: z.number(),
  refraction: ErpOrderRefractionSchema,
  strategy_ref: z.string(),
  tinting: z.string(),
})

export const ErpOrderFrameSchema = z.object({
  attributes: z.object({
    fbrand: z.string(),
    fcatalog: z.string(),
    fcollection: z.string(),
    fcolor: z.string(),
  }),
  default_code: z.string(),
  id: z.number(),
  url: z.string(),
})

export const ErpOrderItemSchema = z.object({
  description: z.string(),
  frl: z.string(),
  id: z.number(),
  price: z.number(),
})

export const ErpOrderPaymentSchema = z.object({
  id: z.number().int().positive().nullish().catch(null),
  amount: z.number(),
  type: z.string(),
  method: z.string(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const ErpOrderGroupSchema = z
  .union([z.string(), z.number()])
  .pipe(z.coerce.number<string | number>().int().positive())
  .nullable()
  .catch(null)

export const ErpOrderHistoryItemSchema = z.object({
  config: ErpOrderHistoryConfigSchema,
  createdAt: z.string(),
  customer_id: z.number(),
  frame: ErpOrderFrameSchema,
  group: ErpOrderGroupSchema,
  id: z.number(),
  invoice_id: z.number(),
  items: z.array(ErpOrderItemSchema),
  note: z.boolean().or(z.string()),
  payments: z.array(ErpOrderPaymentSchema),
  ref: z.string(),
  staff: z.object({
    id: z.number(),
    ref: z.string(),
    name: z.string(),
  }),
  status: z.string(),
  store: z
    .object({
      id: z.number(),
      ref: z.string(),
      name: z.string(),
      type: z.string(),
    })
    .nullish()
    .catch(null),
  updatedAt: z.string(),
})

export const ErpOrderHistoryListSchema = makeErpPaginatedSchema(
  ErpOrderHistoryItemSchema
)

export const ErpOrderDetailSchema = ErpOrderHistoryItemSchema

export type ErpOrderRefraction = z.infer<typeof ErpOrderRefractionSchema>
export type ErpOrderHistoryConfig = z.infer<typeof ErpOrderHistoryConfigSchema>
export type ErpOrderFrame = z.infer<typeof ErpOrderFrameSchema>
export type ErpOrderItem = z.infer<typeof ErpOrderItemSchema>
export type ErpOrderHistoryItem = z.infer<typeof ErpOrderHistoryItemSchema>
export type ErpOrderHistoryList = z.infer<typeof ErpOrderHistoryListSchema>
export type ErpOrderDetail = z.infer<typeof ErpOrderDetailSchema>

export const displayCustomerName = (customer: Customer) => {
  return [customer.first_name, customer.last_name]
    .filter((b) => !!b)
    .join(' ')
    .trim()
}
